import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { LiveKitManager } from "./LiveKitManager";
import { BrandContext } from "../../App";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Snackbar,
  Avatar,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { UnityManager } from "../../services/unity/UnityManager";
import { UnityAudioAnalyzer } from "../../services/unity/UnityAudioAnalyzer";
import { MediaManager } from "../../services/media/MediaManager";
import CallScreenSidePanel from "./CallScreenSidePanel";
import theme from "../../Theme";
import CallToolbar from "../simple/CallToolbar";
import CallScreenModal from "../simple/CallScreenModal";

import PageTitle from "../PageTitle/PageTitle";

import Cookies from "js-cookie";

// determine external mode
const determineExternalMode = (scenarioData, pathname) => {
  const isExternalRoute = pathname.includes("/externalscenario");
  return isExternalRoute && scenarioData?.external_scenario === true;
};

export const CallScreen = ({
  scenarioId = null,
  scenarioData = null,
  isAudio = false,
  user = null,
  firstScenario,
  simulationData,
}) => {
  const resourcesExist =
    scenarioData?.resources && scenarioData.resources.length >= 1;
  const {
    brand,
    trainingProgram,
    authToken,
    isMobile,
    setShowFooter,
    setSideMenuOpen,
    setShowCrumbs
  } = useContext(BrandContext);
  const [showSidePanel, setShowSidePanel] = useState(resourcesExist);
  const [roomName, setRoomName] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [imgSrc, setImgSrc] = useState(scenarioData.image);
  const [transcript, setTranscript] = useState([]);

  const [liveKitManager, setLiveKitManager] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [simulationId, setSimulationId] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showUnityCanvas, setShowUnityCanvas] = useState(true); // default to true for auto-loading
  const [currentMessage, setCurrentMessage] = useState("");
  const [open, setOpen] = useState(false);

  const [unityLoaded, setUnityLoaded] = useState(false);
  const [avatarLoaded, setAvatarLoaded] = useState(false);

  const [avatarSpeaking, setAvatarSpeaking] = useState(false);

  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const transcriptRef = useRef(null);
  const unityInstance = useRef(null);
  const originalConsoleLog = useRef(window.console.log);

  // source of truth for external mode
  const isExternalMode = determineExternalMode(
    scenarioData,
    window.location.pathname
  );

  const [videoPosition, setVideoPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [panelValue, setPanelValue] = useState(resourcesExist ? 1 : 0);
  const [onboardingOpen, setOnboardingOpen] = useState(
    firstScenario == null ? true : firstScenario
  );
  
  
  const [hasCompletedSim, setHasCompletedSim] = useState(false);

  const params = useParams();
  const urlScenarioId = params.scenario_id;
  const effectiveScenarioId = scenarioId || urlScenarioId;
  const DEVELOPMENT_UI_ONLY_MODE = false; // Set to true for UI development

  const [unityManager, setUnityManager] = useState(null);
  const [audioAnalyzer, setAudioAnalyzer] = useState(null);
  const [mediaManager, setMediaManager] = useState(null);
  const media = DEVELOPMENT_UI_ONLY_MODE ? null : new MediaManager();

  const isLandscapeMode = window.matchMedia("(orientation: landscape)").matches;

  const getClient = (event) => {
    if (event.touches && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: event.clientX, y: event.clientY };
  };

  const handleDown = (event) => {
    const { x, y } = getClient(event);
    setIsDragging(true);
    startXRef.current = x - videoPosition.x;
    startYRef.current = y - videoPosition.y;
  };

  function getInitials(user) {
    if (!user) return "Guest";
    return `${user.first_name?.[0] ?? ""}${
      user.last_name?.[0] ?? ""
    }`.toUpperCase();
  }

  const handlePopup = () => {
    setShowSidePanel((prev) => !prev);
  };

  useEffect(() => {
    if (isLandscapeMode) setSideMenuOpen(false);
    setShowFooter(false);
    setShowCrumbs(false);
    if (isMobile) setSideMenuOpen(true);
    if (DEVELOPMENT_UI_ONLY_MODE) return;


    if (Cookies.get("GN-CompletedSim")) {
      setHasCompletedSim(true)
    }

    originalConsoleLog.current = window.console.log;
    window.console.log = function (message) {
      originalConsoleLog.current.apply(console, arguments);
      const logString = Array.from(arguments).join(" ");
      if (
        (typeof message === "string" &&
          message.includes("Initialised camFollow and speechController")) ||
        (logString.includes("avatar") && logString.includes("loaded"))
      ) {
        originalConsoleLog.current("[Hook] Unity avatar fully loaded.");
        setAvatarLoaded(true);
      }

      if (logString.includes("Track subscribed: audio")) {
        originalConsoleLog.current("[Hook] Audio track subscribed.");
        setIsSpeaking(true);
      }
    };

    return () => {
      window.console.log = originalConsoleLog.current;
      setShowCrumbs(true)
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      // Lock scroll
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
      document.body.style.overscrollBehavior = "none";
    }

    return () => {
      // Unlock scroll when unmounting or condition changes
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "auto";
      document.body.style.overscrollBehavior = "";
    };
  }, [isMobile]);

  useEffect(() => {
    if (DEVELOPMENT_UI_ONLY_MODE) return;
    const unity = new UnityManager(canvasRef);
    const analyzer = new UnityAudioAnalyzer(unity);

    setUnityManager(unity);
    setAudioAnalyzer(analyzer);
    setMediaManager(media);

    const manager = new LiveKitManager(trackHandler);

    manager.setTranscriptHandler((raw) => {
      const parsed = JSON.parse(raw);
      setTranscript((prev) => [...prev, parsed]);
    });

    setLiveKitManager(manager);

    // tiemaout to ensure the DOM is ready
    if (!isAudio) {
      setTimeout(() => {
        initializeUnity();
      }, 500);
    }

    // cleanup on unmount
    return () => {
      if (analyzer) analyzer.stop();
      if (unity) unity.forceCleanup();
      if (media) media.cleanup();

      // liveKit cleanup
      manager.cleanup();
      const remoteAudio = document.getElementById("remote-audio");
      if (remoteAudio) remoteAudio.remove();
    };
  }, []);

  // UI Testing mode
  useEffect(() => {
    if (DEVELOPMENT_UI_ONLY_MODE) {
      setIsConnected(true);
      setIsLoading(false);
      setIsConnecting(false);
    }
  }, []);

  // connect to LiveKit when avatar is loaded
  useEffect(() => {
    if (DEVELOPMENT_UI_ONLY_MODE) return;
    if (
      avatarLoaded &&
      liveKitManager &&
      effectiveScenarioId &&
      !isConnected &&
      !isConnecting &&
      !onboardingOpen
    ) {
      console.log(
        "==> Avatar loaded and onboarding complete: connecting to LiveKit"
      );
      connectToLiveKit();
    }
  }, [
    avatarLoaded,
    effectiveScenarioId,
    isConnected,
    isConnecting,
    onboardingOpen,
  ]);

  useEffect(() => {
    if (DEVELOPMENT_UI_ONLY_MODE) return;
    if (isAudio && liveKitManager && !onboardingOpen) {
      console.log("==> Audio only: connecting to LiveKit");
      connectToLiveKit();
    }
  }, [liveKitManager, onboardingOpen]);

  useEffect(() => {
    if (DEVELOPMENT_UI_ONLY_MODE) return;
    if (unityLoaded && !avatarLoaded) {
      const timeoutId = setTimeout(() => {
        if (!avatarLoaded) {
          console.log("==> Avatar load timeout - forcing connection");
          setAvatarLoaded(true);
        }
      }, 60 * 10000);

      return () => clearTimeout(timeoutId);
    }
  }, [unityLoaded, avatarLoaded]);

  const setupCamera = () => {
    if (!media || isAudio) return;

    console.log("==> Setting up webcam");

    media.setupLocalVideo(videoRef);

    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        console.log("==> Webcam access granted");
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setVideoEnabled(true);
      })
      .catch((err) => {
        console.error("==> Webcam access error:", err);
        setVideoEnabled(false);
      });
  };

  useEffect(() => {
    if (videoEnabled && videoRef.current && !videoRef.current.srcObject) {
      console.log("==> Ensuring video stream is attached after render");

      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          videoRef.current.srcObject = stream;
        })
        .catch((err) => {
          console.error("==> Webcam access error during post-render fix:", err);
        });
    }
  }, [videoEnabled, isConnected, isLoading, isConnecting]);

  const trackHandler = useCallback(({ track, streams, participant }) => {
    console.log("Track handler:", track, streams, participant);
    if (track.kind === "video" && videoRef.current) {
      const stream = streams[0];
      if (stream) {
        videoRef.current.srcObject = stream;
      } else {
        videoRef.current.srcObject = new MediaStream([track.mediaStreamTrack]);
      }
    }
    if (track.kind === "audio") {
      let audioElement = document.getElementById("remote-audio");
      if (!audioElement) {
        audioElement = document.createElement("audio");
        audioElement.id = "remote-audio";
        audioElement.autoplay = true;
        document.body.appendChild(audioElement);
      }
      const stream = streams[0];

      if (stream) {
        audioElement.srcObject = stream;
      } else {
        audioElement.srcObject = new MediaStream([track.mediaStreamTrack]);
      }
      setupAudioAnalysis(track.mediaStreamTrack);
    }
  }, []);

  const setupAudioAnalysis = (mediaTrack) => {
    if (DEVELOPMENT_UI_ONLY_MODE) return;

    if (!mediaTrack) return;
    try {
      const audioContext = new AudioContext();
      const mediaStream = new MediaStream([mediaTrack]);
      const source = audioContext.createMediaStreamSource(mediaStream);
      const analyser = audioContext.createAnalyser();
      source.connect(analyser);

      // Tell Unity to grab audio from the browser's audiocontext
      window.SpeechAudioSource = source;
      window.SpeechAudioContext = audioContext;
      unityInstance.current.SendMessage(
        "WebGLAudioBridge",
        "OnSetAudioContext"
      );

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let spoken = false;

      function detectAudio() {
        analyser.getByteFrequencyData(dataArray);
        const hasAudio = dataArray.some((value) => value > 0);
        if (hasAudio) {
          if (unityInstance.current) {
            unityInstance.current.SendMessage(
              "AvatarManager",
              "OnSpeech",
              dataArray.toString()
            );
          }
          spoken = true;
          setAvatarSpeaking(true);
        } else if (spoken) {
          if (unityInstance.current) {
            unityInstance.current.SendMessage("AvatarManager", "OnStopSpeech");
          }
          spoken = false;
          setAvatarSpeaking(false);
        }
        requestAnimationFrame(detectAudio);
      }
      detectAudio();
    } catch (error) {
      console.error("Error setting up audio analysis:", error);
    }
  };

  // initialize unity automatically
  const initializeUnity = async (avatarName = "Default") => {
    if (DEVELOPMENT_UI_ONLY_MODE) return;

    try {
      console.log("==> Unity initialization started");
      console.log("==> Canvas reference:", canvasRef.current);

      if (!window.createUnityInstance) {
        console.error("createUnityInstance not available!");
        setError("Unity loader not available. Please reload the page.");
        return;
      }

      if (!canvasRef.current) {
        console.error("Canvas reference is null!");
        setError("Canvas element not found. Please reload the page.");
        return;
      }

      // Clear any previous error
      setError(null);
      setIsLoading(true);

      // Generate a cache-busting query parameter (e.g., timestamp or random string)
      const cacheBuster = `?_=${Date.now()}`; // Unique timestamp for each load
      // Alternatively, use a random string: `?_=${Math.random().toString(36).substring(2)}`

      // Update Unity asset URLs with cache-busting query params
      const instance = await window.createUnityInstance(canvasRef.current, {
        dataUrl: `/avatar/Build/GuruNowBuild.data.unityweb${cacheBuster}`,
        frameworkUrl: `/avatar/Build/GuruNowBuild.framework.js.unityweb${cacheBuster}`,
        codeUrl: `/avatar/Build/GuruNowBuild.wasm.unityweb${cacheBuster}`,
        streamingAssetsUrl: `/avatar/StreamingAssets`, // May not apply to all StreamingAssets
        companyName: "GuruNow",
        productName: "GuruNow AI",
        productVersion: "0.1",
        matchWebGLToCanvasSize: false,
      });

      console.log("==> Unity instance created successfully");
      unityInstance.current = instance;
      window.unityInstance = instance;

      const fallbackAvatarName =
        scenarioData?.avatar_persona?.avatar_model || avatarName || "Default";
      const sceneName = scenarioData?.scene_name || "GuruNow Simulation";

      console.log(
        `==> Loading Unity scene: ${sceneName}/${fallbackAvatarName}`
      );
      unityInstance.current.SendMessage(
        "SceneLoader",
        "LoadScene",
        String(sceneName + "/" + fallbackAvatarName)
      );

      if (media && !isAudio) {
        media.setupLocalVideo(videoRef);
      }

      setIsLoading(false);
      setUnityLoaded(true);
      console.log(
        "==> Unity initialized successfully - waiting for avatar to load"
      );
    } catch (error) {
      console.error("Failed to initialize Unity:", error);
      setError("Unity initialization failed: " + error.message);
      setIsLoading(false);
    }
  };

  const manualConnect = () => {
    if (!unityLoaded) {
      setSnackbarMessage(
        "Unity not loaded yet. Please wait for Unity to initialize first."
      );
      return;
    }

    console.log("==> Manually triggering LiveKit connection");
    setAvatarLoaded(true);
  };

  const connectToLiveKit = async () => {
    if (DEVELOPMENT_UI_ONLY_MODE) return;
    if (!liveKitManager || !isAudio) {
      if (isConnecting || !effectiveScenarioId) {
        console.log("==> Cannot connect to LiveKit");
        return;
      }
    }
    setIsConnecting(true);
    setError(null);

    try {
      // simple mode check
      if (isExternalMode && scenarioData?.useProvidedToken) {
        console.log("==> Using provided token for external scenario");
        if (scenarioData.simulation_id) {
          setSimulationId(scenarioData.simulation_id);
          setRoomName(scenarioData.simulation_id || "LiveKit-Room");
        } else {
          console.warn("No simulation_id provided for external simulation");
        }

        if (!scenarioData.livekit_url) {
          throw new Error("No LiveKit URL provided for external simulation");
        }

        await liveKitManager.connect(scenarioData.livekit_url, {
          token: scenarioData.token,
        });
        setIsConnected(true);
        console.log(
          "==> Connected to LiveKit successfully with provided token"
        );
        await handleEnableAudio();
        setupCamera();
        return;
      }

      // regular scenarios
      const start_simulation_endpoint = isExternalMode
        ? "start_simulation_external"
        : "start_simulation";
      const data = simulationData;

      if (!data["token"]) {
        throw new Error("No token or URL received from server");
      }

      setSimulationId(data.id);
      setRoomName(data.id || "LiveKit-Room");

      await liveKitManager.connect(data["livekit_url"], {
        token: data["token"],
      });
      setIsConnected(true);
      console.log("==> Connected to LiveKit successfully");

      // enable audio when connected
      await handleEnableAudio();

      setupCamera();

      if (data.avatar_name && unityInstance.current) {
        const sceneName = scenarioData?.scene_name || "GuruNow Simulation";
        unityInstance.current.SendMessage(
          "SceneLoader",
          "LoadScene",
          String(sceneName + "/" + data.avatar_name)
        );
      }
    } catch (error) {
      console.error("LiveKit connection error:", error);
      setError(error.message);
      setSnackbarMessage("Connection to LiveKit failed: " + error.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = useCallback(() => {
    if (liveKitManager) {
      liveKitManager.cleanup();
      setIsConnected(false);
      setAudioEnabled(false);
    }
  }, [liveKitManager]);

  const handleEndSimulation = useCallback(async () => {
    if (DEVELOPMENT_UI_ONLY_MODE) return;
    const effectiveSimulationId = simulationId || scenarioData?.simulationId;
    if (!effectiveSimulationId) {
      setSnackbarMessage("No active simulation to end");
      return;
    }

    // endpoint determination
    let end_simulation_endpoint = isExternalMode
      ? "rtc_end_external_simulation"
      : "rtc_end_simulation";

    try {
      await fetch(`${brand.api_base_url}/${end_simulation_endpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Token ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          simulation_id: effectiveSimulationId,
        }),
      });

      handleDisconnect();
      setSnackbarMessage("Simulation ended successfully");
      setSimulationId(null);

      // clean up Unity
      if (unityInstance.current) {
        try {
          unityInstance.current.Quit();
          unityInstance.current = null;
        } catch (e) {
          console.warn("Error cleaning up Unity instance:", e);
        }
      }

      // redirection logic
      if (isExternalMode) {
        // external mode redirection logic
        const redirectPath = scenarioData.has_scoring_engine
          ? `/externalscenario_feedback/${effectiveSimulationId}`
          : `/externalscenario/${scenarioData.id}`;
        window.location.href = redirectPath;
      } else {
        // TODO: remove hack; add proper scenario type to scenarioData
        const isLesson =
          scenarioData.resources !== null &&
          Array.isArray(scenarioData.resources);
        if (isLesson) {
          let targetScenarioId = null;
          if (trainingProgram?.steps) {
            let currentFound = false;
            outerLoop: for (const step of trainingProgram.steps) {
              const chapter = step.chapter;
              if (!chapter?.steps) continue;
              for (const scenarioStep of chapter.steps) {
                const scenario = scenarioStep.scenario;
                if (scenario.id === scenarioData.id) {
                  currentFound = true;
                  continue;
                }
                if (!currentFound) {
                  continue;
                }
                targetScenarioId = scenario.id;
                break outerLoop;
              }
            }
          }
          if (targetScenarioId) {
            window.location.href = `/dashboard/simulate/${targetScenarioId}`;
          } else {
            // Redirect to the program view if the lesson was the last scenario in the program
            window.location.href = `/dashboard/trainingprogram/${trainingProgram.id}`;
          }
        } else if (authToken) {
          window.location.href = `/dashboard/feedback/${effectiveSimulationId}`;
        } else {
          // redirect to login or home
          console.warn(
            "No authentication token available for internal mode redirect"
          );
          window.location.href = "/login"; // or another appropriate page
        }
      }
    } catch (error) {
      console.error(
        "Error determining redirect after ending simulation:",
        error
      );
      setSnackbarMessage(
        `Failed to redirect after simulation: ${error.message}`
      );
    }
  }, [
    DEVELOPMENT_UI_ONLY_MODE,
    simulationId,
    scenarioData,
    isExternalMode,
    brand.api_base_url,
    authToken,
    handleDisconnect,
  ]);

  const handleEnableAudio = async () => {
    if (DEVELOPMENT_UI_ONLY_MODE) return;
    if (liveKitManager) {
      try {
        await liveKitManager.setMicrophoneEnabled(true);
        setAudioEnabled(true);
      } catch (error) {
        console.error("Error enabling microphone:", error);
        setError("Failed to enable audio: " + error.message);
      }
    }
  };

  const handleToggleVideo = async () => {
    const newState = !videoEnabled;
    setVideoEnabled(newState);

    if (!media || isAudio) return;

    if (newState) {
      // Attempt to turn ON webcam
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Failed to enable webcam:", err);
        setVideoEnabled(false);
      }
    } else {
      // Turn OFF webcam
      const currentStream = videoRef.current?.srcObject;
      if (currentStream && currentStream.getTracks) {
        currentStream.getTracks().forEach((track) => track.stop());
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const handleTranscript = () => {
    setOpen(true);
    setTimeout(() => {
      transcriptRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
    if (!open) {
      setTimeout(() => {
        transcriptRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    }
  };

  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const EDGE_MARGIN = 16; // px spacing from edges

  const handleMouseDown = (event) => {
    setIsDragging(true);
    startXRef.current = event.clientX - videoPosition.x;
    startYRef.current = event.clientY - videoPosition.y;
  };

  useEffect(() => {
    const handleMove = (event) => {
      if (!isDragging) return;
      const { x, y } = getClient(event);
      const newX = x - startXRef.current;
      const newY = y - startYRef.current;
      setVideoPosition({ x: newX, y: newY });
    };

    const handleUp = () => {
      if (!isDragging) return;
      setIsDragging(false);

      const videoEl = videoRef.current;
      if (!videoEl) return;

      const rect = videoEl.getBoundingClientRect();
      const isVideoElement = videoEl.id === "video"; // Check if it's the video or avatar
      const videoWidth = isVideoElement ? 182 : 182; // Desktop sizes from sx prop
      const videoHeight = isVideoElement ? 182 : 165; // Desktop sizes from sx prop

      const vw = window.innerWidth;
      const vh = window.innerHeight;

      if (isMobile) {
        // Mobile: Snap to window corners, adjust for mobile sizes
        const mobileVideoWidth = isVideoElement ? 103 : 103; // Mobile sizes from sx prop
        const mobileVideoHeight = isVideoElement ? 103 : 96; // Mobile sizes from sx prop
        const corners = [
          { x: EDGE_MARGIN, y: EDGE_MARGIN }, // top-left
          { x: vw - mobileVideoWidth - EDGE_MARGIN, y: EDGE_MARGIN }, // top-right
          { x: EDGE_MARGIN, y: vh - (mobileVideoHeight + 70) - EDGE_MARGIN }, // bottom-left
          {
            x: vw - mobileVideoWidth - EDGE_MARGIN,
            y: vh - (mobileVideoHeight + 70) - EDGE_MARGIN,
          }, // bottom-right
        ];

        let closest = corners[0];
        let minDist = Infinity;

        corners.forEach((corner) => {
          const dx = corner.x - videoPosition.x;
          const dy = corner.y - videoPosition.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < minDist) {
            minDist = dist;
            closest = corner;
          }
        });

        setVideoPosition(closest);
      } else {
        // Desktop: Snap to callscreen-container corners
        const containerEl = document.getElementById("callscreen-container");
        if (!containerEl) return;

        const containerRect = containerEl.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        // Snap points relative to the container's top-left corner with EDGE_MARGIN
        const corners = [
          { x: EDGE_MARGIN, y: EDGE_MARGIN }, // top-left
          { x: containerWidth - videoWidth - EDGE_MARGIN, y: EDGE_MARGIN }, // top-right
          { x: EDGE_MARGIN, y: containerHeight - videoHeight - EDGE_MARGIN }, // bottom-left
          {
            x: containerWidth - videoWidth - EDGE_MARGIN,
            y: containerHeight - videoHeight - EDGE_MARGIN,
          }, // bottom-right
        ];

        // Ensure snap points stay within container bounds
        const boundedCorners = corners.map((corner) => ({
          x: Math.max(0, Math.min(corner.x, containerWidth - videoWidth)),
          y: Math.max(0, Math.min(corner.y, containerHeight - videoHeight)),
        }));

        // Find the closest corner
        let closest = boundedCorners[0];
        let minDist = Infinity;

        boundedCorners.forEach((corner) => {
          // Adjust videoPosition to container-relative coordinates for distance calculation
          const containerRelativeX = videoPosition.x - containerRect.left;
          const containerRelativeY = videoPosition.y - containerRect.top;
          const dx = corner.x - containerRelativeX;
          const dy = corner.y - containerRelativeY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < minDist) {
            minDist = dist;
            closest = corner;
          }
        });

        // Set position relative to the container
        setVideoPosition({
          x: closest.x,
          y: closest.y,
        });
      }
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("touchend", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, videoPosition]);

  useEffect(() => {
    if (!liveKitManager) return;

    liveKitManager.setRPCHandler((payload, participant, kind) => {
      if (
        payload.type === "end_conversation" &&
        payload.message === "END_SIMULATION"
      ) {
        console.log("==> Received simulation termination message from RPC");
        setSnackbarMessage(
          "Simulation ended by " + payload.caller || "unknown"
        );
        try {
          setTimeout(handleEndSimulation, payload.timeout * 1000 || 5000);
          return JSON.stringify({
            status: "ACK",
          });
        } catch (e) {
          console.error("Error handling end simulation timeout:", e);
          return JSON.stringify({
            status: "FAIL",
          });
        }
      }
      return "IGN";
    });
  }, [liveKitManager, handleEndSimulation]);

  console.log("has completed", hasCompletedSim)

  return (
    <Box
      id="callscreen-container"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        height: "100%",
        backgroundColor: "transparent",
        mt: scenarioData.external_scenario ? "0px" : 0,
        "& #video": {
          width: "182px",
          height: "182px",
          [theme.breakpoints.down("md")]: {
            width: "103px",
            height: "103px",
          },
        },
        "& #avatar-pic": {
          width: "182px",
          height: "165px",
          [theme.breakpoints.down("md")]: {
            width: "103px",
            height: "96px",
          },
        },
      }}
    >
      <Snackbar
        open={snackbarMessage ? true : false}
        autoHideDuration={5000}
        onClose={() => {
          setSnackbarMessage("");
        }}
        message={snackbarMessage}
      />

      {/* main container */}
      <Paper
        elevation={3}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "1350px",
          aspectRatio: "16 / 9",
          borderRadius: 2,
          backgroundColor: !isAudio ? "#231F20" : "#d8d8d8",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          overflow: "hidden",
          filter: !hasCompletedSim ? "blur(2px)" : "none",
          [theme.breakpoints.down("md")]: {
            height: "calc(100vh - 70px)",
            aspectRatio: "9 / 16",
            borderRadius: "0",
            position: "absolute",
            bottom: 0,
            transition: "all 0.2s ease",
          },
          "@media (max-width: 740px) and (orientation: landscape) and (pointer: coarse)": {
            height: "100vh"
          }
        }}
      >
        {/* unity canvas */}
        <canvas
          ref={canvasRef}
          id="unity-canvas"
          width={960}
          height={600}
          tabIndex={-1}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            touchAction: "none",
          }}
        />

        {isAudio ? null : videoEnabled ? (
          <video
            ref={videoRef}
            id="video"
            draggable={false}
            autoPlay
            playsInline
            muted
            style={{
              position: "absolute",
              top: videoPosition.y,
              left: videoPosition.x,
              objectFit: "cover",
              borderRadius: "8px",
              cursor: isDragging ? "grabbing" : "grab",
              backgroundColor: "black",
              zIndex: 500,
              display:
                isConnected && !isLoading && !isConnecting ? "block" : "none",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
              transition: isDragging ? "none" : "top 0.2s, left 0.2s", // smooth snap
              transform: "rotateY(180deg)",
              WebkitTransform: "rotateY(180deg)", /* Safari and Chrome */
              MozTransform: "rotateY(180deg)", /* Firefox */
            }}
            onMouseDown={handleDown}
            onTouchStart={handleDown}
          />
        ) : (
          <Avatar
            id="avatar-pic"
            onMouseDown={handleMouseDown}
            sx={{
              position: "absolute",
              top: videoPosition.y,
              left: videoPosition.x,
              width: "182px",
              height: "165px",
              cursor: isDragging ? "grabbing" : "grab",
              borderRadius: "8px",
              backgroundColor: "black",
              zIndex: 500,
              display:
                isConnected && !isLoading && !isConnecting ? "flex" : "none",
              fontSize: "48px",
              color: "grey",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
            }}
          />
        )}

        {/* loading overlay */}
        {isAudio ? (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              zIndex: 10,
            }}
          >
            <Typography variant="h5" color="black" mb={2}>
              {!isSpeaking ? "Connecting to call..." : "Audio call connected"}
            </Typography>

            {isSpeaking && (
              <Avatar
                src={imgSrc}
                alt="Call Image"
                onError={() => setImgSrc(null)}
                sx={{
                  height: 200,
                  width: 200,
                  backgroundColor: "white",
                  color: "grey",
                }}
              />
            )}
          </Box>
        ) : (
          (isLoading || isConnecting || !isConnected) && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(35, 31, 32, 0.85)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <CircularProgress size={60} sx={{ color: "#ffffff" }} />
              <Typography variant="h5" mt={3} color="white">
                {isConnecting
                  ? "Connecting to simulation..."
                  : isLoading
                  ? "Loading the environment..."
                  : unityLoaded && !avatarLoaded
                  ? "Waiting for call to connect..."
                  : "Completing final touches..."}
              </Typography>

              {/* {unityLoaded && !avatarLoaded && !isConnecting && (
        <>
          <Typography variant="body2" mt={2} color="grey.400">
            Waiting for avatar initialization to complete
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={manualConnect}
            sx={{ mt: 3 }}
          >
            Connect Manually
          </Button>
        </>
      )} */}
            </Box>
          )
        )}

        {(isAudio || (isConnected && !isLoading && !isConnecting)) && (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              gap: 15,
              justifyContent: showSidePanel ? "space-between" : "center",
              alignItems: "flex-end",
              zIndex: 30,
              boxSizing: "border-box",
              padding: `${theme.customSpacing["2xl"]}`,
            }}
          >
            <CallToolbar
              videoEnabled={videoEnabled}
              handleToggleVideo={handleToggleVideo}
              handleEndSimulation={handleEndSimulation}
              openHelpModal={() => setOnboardingOpen(true)}
              panelValue={panelValue}
              setPanelValue={setPanelValue}
              openSidePanel={() => setShowSidePanel(true)}
              scenarioData={scenarioData}
              isExternalMode={isExternalMode}
              isMobile={isMobile}
              isSpeaking={avatarSpeaking}
              onHelpTooltipClose={() => setHasCompletedSim(true)}
            />

            {showSidePanel && (
              <CallScreenSidePanel
                scenarioData={scenarioData}
                isExternalMode={isExternalMode}
                tab={panelValue}
                showSidePanel={showSidePanel}
                onClose={() => setShowSidePanel(false)}
                transcript={transcript}
                isMobile={isMobile}
              />
            )}
          </Box>
        )}
      </Paper>

      {error && (
        <Box sx={{ mt: 2, color: "error.main", position: "absolute" }}>
          <Typography variant="body1">Error: {error}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default CallScreen;